import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  HeaderLinksBase,
  LinksContainer,
  DrawerTitle,
  PageLink,
  BackButton,
  HeaderLinkIconMobile,
  DrawerTitleLabel,
  HeaderLinkIcon,
  DrawerColumnsContainer,
  DrawerColumns,
  DrawerColumn,
  DrawerColumnTitle,
  DrawerColumnItems,
  DrawerColumnItemLink,
  DrawerColumnItem,
  Flyout,
  FlyoutTrigger,
  FlyoutList,
  FlyoutListWrapper,
  FlyoutListInner,
  FlyoutListAlignment,
} from "./header-links.styled";
import {
  HeaderLinksQuery,
  HeaderLinksNodeFragment,
  PrismicHeaderLinksDataBodyDrawerColumn,
  PrismicHeaderLinksDataBodyDrawerButtonPrimary,
} from "../../types/graphql";
import { PrismicRichText } from "@prismicio/react";
import { RichTextField, RTNode } from "@prismicio/types";
import { buildRelativeUrl } from "../../utils/environment-urls";
import { linkTo } from "../../utils/contentUtils";
import { isNavigableUrl } from "./is-navigable-url";
import { track } from "../../utils/analytics";
import { useThemeContext } from "../../utils/contexts/theme-context";
import * as FA from "../../styles/fontawesome";
import clsx from "clsx";
import GatsbyLink from "../link/link";
import { useFlyoutSafeArea } from "./use-flyout-safe-area";
import { RTLinkNode } from "@prismicio/types";

interface IPureHeaderLinksProps {
  headerLinks: HeaderLinksQuery;
  headerLinksUID: string;
  setHeaderLinksData: (data: IStructuredHeaderLinksData) => void;
  isHeaderLinksDataSet: boolean;
  isOpen: boolean;
  showAnnouncementBanner: boolean;
  isDarkMode?: boolean;
  setIsDesktopNavOpen: (isOpen: boolean) => void;
  registerEscapeHandler?: (handler: (() => boolean) | null) => void;
  registerNavCloseHandler?: (handler: (() => void) | null) => void;
  onRequestCloseMobileNav?: () => void;
}

export interface IDrawerData {
  title: string | null | undefined;
  minHeight?: number | null;
  maxHeight?: number | null;
  columns: PrismicHeaderLinksDataBodyDrawerColumn[];
}

export interface IStructuredHeaderLinksData {
  drawers: IDrawerData[];
  pageLinks?: Array<{
    link_text?: string | null;
    link?: { target?: string | null; url?: string | null } | null;
  }>;
}

// Function to extract array of drawer objects.
const getDrawerDataStructure = (
  links?: HeaderLinksNodeFragment
): IDrawerData[] => {
  let drawerData: IDrawerData[] = [];

  let i = 0;
  let slices = links?.data?.body;
  while (slices && i < slices.length && slices[i]) {
    if (slices[i]?.slice_type === "drawer_button") {
      const drawerButtonPrimary = slices[i]
        ?.primary as PrismicHeaderLinksDataBodyDrawerButtonPrimary;
      // Push a drawer object onto result array with title and empty columns array
      drawerData.push({
        title: drawerButtonPrimary?.drawer_button_text,
        minHeight: drawerButtonPrimary?.min_height,
        maxHeight: drawerButtonPrimary?.max_height,
        columns: [],
      });
      let drawerColumns = [];
      i++;
      // Loop over all drawer column slices pushing column data into columns array
      while (i < slices.length && slices[i]?.slice_type !== "drawer_button") {
        if (slices[i]?.slice_type === "drawer_column")
          drawerColumns.push(
            slices[i] as PrismicHeaderLinksDataBodyDrawerColumn
          );
        i++;
      }
      drawerData[drawerData.length - 1].columns.push(...drawerColumns);
    }
  }

  return drawerData;
};

// Groups a Prismic rich text field into paragraph + list pairs.
// Each new paragraph starts a new section so flyouts can be toggled independently.
const splitRichTextSections = (field: RichTextField): RichTextField[] => {
  if (!field?.length) return [];

  const sections: RichTextField[] = [];
  let currentSection: RTNode[] = [];

  for (const block of field) {
    if (block.type === "paragraph" && currentSection.length > 0) {
      sections.push(currentSection as RichTextField);
      currentSection = [block];
    } else {
      currentSection.push(block);
    }
  }

  if (currentSection.length > 0) {
    sections.push(currentSection as RichTextField);
  }

  return sections;
};

const sectionHasList = (section: RichTextField): boolean =>
  section.some(
    (block) => block.type === "list-item" || block.type === "o-list-item"
  );

const columnHasList = (
  items: PrismicHeaderLinksDataBodyDrawerColumn["items"]
): boolean =>
  items?.some((item) =>
    item?.link?.richText
      ? splitRichTextSections(item.link.richText).some(sectionHasList)
      : false
  ) ?? false;

const getSectionLabel = (section: RichTextField): string => {
  const paragraph = section.find((block) => block.type === "paragraph");

  if (paragraph && "text" in paragraph) {
    return paragraph.text.trim();
  }

  return "";
};

const resolvePrismicLinkUrl = (hyperlink: RTLinkNode): string => {
  if (hyperlink.data?.link_type === "Document") {
    return buildRelativeUrl(linkTo(hyperlink.data));
  }

  if (
    hyperlink.data?.link_type === "Web" ||
    hyperlink.data?.link_type === "Media"
  ) {
    return buildRelativeUrl(hyperlink.data.url || "");
  }

  return "";
};

const trackHeaderNavLinkClick = (
  event: React.MouseEvent<HTMLElement>,
  url: string,
  drawerTitle?: string | null,
  columnTitle?: string | null
) => {
  const inFlyout = event.currentTarget.closest("[data-flyout-list]");

  track("Header Nav Link Clicked", {
    link_text: event.currentTarget.textContent?.trim() || "",
    link_url: url,
    link_location: inFlyout ? "flyout" : "drawer",
    drawer_title: drawerTitle ?? "",
    column_title: columnTitle ?? "",
  });
};

const XL_MEDIA_QUERY = "(min-width: 1280px)";
const MOBILE_NAV_QUERY = "(max-width: 767px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches;

const FLYOUT_FADE_MS = 250;

const getFlyoutListAlignment = (
  itemIndex: number,
  totalItems: number
): FlyoutListAlignment => {
  if (totalItems <= 1) {
    return "center";
  }

  const ratio = itemIndex / (totalItems - 1);

  if (ratio < 1 / 3) {
    return "flex-start";
  }

  if (ratio < 2 / 3) {
    return "center";
  }

  return "flex-end";
};

const resolveFlyoutKeyFromTarget = (
  target: EventTarget | null
): string | null | undefined => {
  if (!(target instanceof HTMLElement)) {
    return undefined;
  }

  const panel = target.closest("[data-flyout-list]");
  if (panel?.id?.startsWith("header-flyout-")) {
    return panel.id.slice("header-flyout-".length);
  }

  const trigger = target.closest("[id^='header-flyout-trigger-']");
  if (trigger?.id) {
    return trigger.id.slice("header-flyout-trigger-".length);
  }

  if (target.closest("[data-flyout-plain-row]")) {
    return null;
  }

  return undefined;
};

const FlyoutListAccordion = ({
  isOpen,
  $alignment,
  $flyoutKey,
  flyoutListId,
  ariaLabel,
  children,
}: {
  isOpen: boolean;
  $alignment: FlyoutListAlignment;
  $flyoutKey: string;
  flyoutListId: string;
  ariaLabel: string;
  children: React.ReactNode;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [expanded, setExpanded] = useState(isOpen);
  const [visible, setVisible] = useState(false);

  const measureContent = useCallback(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, []);

  useLayoutEffect(() => {
    measureContent();
  }, [measureContent, isOpen, children]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const resizeObserver = new ResizeObserver(measureContent);
    resizeObserver.observe(content);

    return () => resizeObserver.disconnect();
  }, [measureContent]);

  useEffect(() => {
    if (isOpen) {
      setExpanded(true);

      if (prefersReducedMotion()) {
        setVisible(true);
        return;
      }

      setVisible(false);

      // Two frames so the browser paints opacity: 0 before transitioning to 1.
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });

      return () => cancelAnimationFrame(raf);
    }

    if (prefersReducedMotion()) {
      setVisible(false);
      setExpanded(false);
      return;
    }

    setVisible(false);
    const timer = setTimeout(() => setExpanded(false), FLYOUT_FADE_MS);

    return () => clearTimeout(timer);
  }, [isOpen]);

  return (
    <FlyoutListWrapper $expanded={expanded} $height={contentHeight}>
      <FlyoutListInner
        ref={contentRef}
        $visible={visible}
        $alignment={$alignment}
        $flyoutKey={$flyoutKey}
        data-flyout-list=""
        data-flyout-align={$alignment}
        id={flyoutListId}
        aria-label={ariaLabel}
      >
        {children}
      </FlyoutListInner>
    </FlyoutListWrapper>
  );
};

// Flyout lists are absolutely positioned, so they do not affect column layout height.
// Measure the tallest list in each column and reserve that space at xl+ to prevent drawer jump on hover.
const MeasuredDrawerColumn = ({
  isDrawerOpen,
  $hasList,
  children,
}: {
  isDrawerOpen: boolean;
  $hasList?: boolean;
  children: React.ReactNode;
}) => {
  const containerRef = useRef<HTMLLIElement>(null);
  const [flyoutMinHeight, setFlyoutMinHeight] = useState<number>();

  const measureFlyoutLists = useCallback(() => {
    const container = containerRef.current;
    if (
      !container ||
      !isDrawerOpen ||
      !window.matchMedia(XL_MEDIA_QUERY).matches
    ) {
      // min-height is only needed for the desktop side-panel flyout layout.
      setFlyoutMinHeight(undefined);
      return;
    }

    const flyoutLists =
      container.querySelectorAll<HTMLElement>("[data-flyout-list]");
    let maxHeight = 0;

    flyoutLists.forEach((list) => {
      maxHeight = Math.max(maxHeight, list.scrollHeight);
    });

    setFlyoutMinHeight(maxHeight > 0 ? maxHeight : undefined);
  }, [isDrawerOpen]);

  useEffect(() => {
    measureFlyoutLists();

    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(measureFlyoutLists);
    container
      .querySelectorAll("[data-flyout-list]")
      .forEach((list) => resizeObserver.observe(list));

    const xlQuery = window.matchMedia(XL_MEDIA_QUERY);
    xlQuery.addEventListener("change", measureFlyoutLists);
    window.addEventListener("resize", measureFlyoutLists);

    return () => {
      resizeObserver.disconnect();
      xlQuery.removeEventListener("change", measureFlyoutLists);
      window.removeEventListener("resize", measureFlyoutLists);
    };
  }, [measureFlyoutLists, children]);

  return (
    <DrawerColumn
      ref={containerRef}
      $hasList={$hasList}
      $flyoutMinHeight={flyoutMinHeight}
    >
      {children}
    </DrawerColumn>
  );
};

type DrawerColumnItemRichTextProps = {
  field: RichTextField;
  linkId: string;
  canUseFlyoutToggle: boolean;
  openFlyoutKey: string | null;
  setOpenFlyoutKey: (key: string | null) => void;
  drawerTitle?: string | null;
  columnTitle?: string | null;
  flyoutListAlignment: FlyoutListAlignment;
};

const drawerColumnItemRichTextPropsAreEqual = (
  prev: DrawerColumnItemRichTextProps,
  next: DrawerColumnItemRichTextProps
) => {
  const sharedEqual =
    prev.field === next.field &&
    prev.linkId === next.linkId &&
    prev.canUseFlyoutToggle === next.canUseFlyoutToggle &&
    prev.flyoutListAlignment === next.flyoutListAlignment &&
    prev.drawerTitle === next.drawerTitle &&
    prev.columnTitle === next.columnTitle;

  if (!sharedEqual) {
    return false;
  }

  if (prev.canUseFlyoutToggle || next.canUseFlyoutToggle) {
    return prev.openFlyoutKey === next.openFlyoutKey;
  }

  return true;
};

const DrawerColumnItemRichText = memo(function DrawerColumnItemRichText({
  field,
  linkId,
  canUseFlyoutToggle,
  openFlyoutKey,
  setOpenFlyoutKey,
  drawerTitle,
  columnTitle,
  flyoutListAlignment,
}: DrawerColumnItemRichTextProps) {
  const sections = splitRichTextSections(field);

  return (
    <>
      {sections.map((section, sectionIndex) => {
        // Unique key across drawers, columns, items, and paragraph/list sections.
        const flyoutKey = `${linkId}-${sectionIndex}`;
        const hasList = sectionHasList(section);
        const sectionLabel = getSectionLabel(section);
        const flyoutListId = `header-flyout-${flyoutKey}`;
        const isFlyoutOpen = openFlyoutKey === flyoutKey;

        return (
          <Flyout key={flyoutKey} data-flyout-key={flyoutKey}>
            <PrismicRichText
              field={section}
              components={{
                paragraph: ({ children, ...props }) => {
                  if (hasList) {
                    return (
                      <FlyoutTrigger
                        {...props}
                        id={`header-flyout-trigger-${flyoutKey}`}
                        $flyoutKey={flyoutKey}
                        // Keyboard-focusable in every mode; keyboard (below) and
                        // touch toggle the flyout, desktop mouse uses hover.
                        role="button"
                        tabIndex={0}
                        aria-expanded={
                          canUseFlyoutToggle ? isFlyoutOpen : undefined
                        }
                        aria-controls={flyoutListId}
                        onClick={(e) => {
                          if (!canUseFlyoutToggle) {
                            return;
                          }
                          // Let link clicks navigate; only toggle when tapping the label text.
                          if ((e.target as HTMLElement).closest("a")) {
                            return;
                          }
                          // Prevent DrawerColumnItemLink from closing the drawer on toggle.
                          e.stopPropagation();
                          const willOpen = openFlyoutKey !== flyoutKey;
                          setOpenFlyoutKey(willOpen ? flyoutKey : null);
                          track("Header Nav Flyout Toggled", {
                            is_open: willOpen,
                            drawer_title: drawerTitle ?? "",
                            column_title: columnTitle ?? "",
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            const willOpen = openFlyoutKey !== flyoutKey;
                            setOpenFlyoutKey(willOpen ? flyoutKey : null);
                            track("Header Nav Flyout Toggled", {
                              is_open: willOpen,
                              drawer_title: drawerTitle ?? "",
                              column_title: columnTitle ?? "",
                            });
                          }
                        }}
                      >
                        {children}
                        <i
                          className="text-xs ml-2"
                          css={FA.AngleRightSolid}
                          aria-hidden="true"
                        />
                      </FlyoutTrigger>
                    );
                  }

                  return (
                    <p {...props} data-flyout-plain-row="">
                      {children}
                    </p>
                  );
                },
                list: ({ children, ...props }) => {
                  return (
                    // Accordion animation is mobile/touch-only; desktop reveals
                    // the absolute panel via the FlyoutListInner `$isOpen` prop.
                    <FlyoutListAccordion
                      isOpen={canUseFlyoutToggle && isFlyoutOpen}
                      $alignment={flyoutListAlignment}
                      $flyoutKey={flyoutKey}
                      flyoutListId={flyoutListId}
                      ariaLabel={
                        sectionLabel ? `${sectionLabel} links` : "Submenu links"
                      }
                    >
                      <FlyoutList $flyoutKey={flyoutKey} {...props}>
                        {children}
                      </FlyoutList>
                    </FlyoutListAccordion>
                  );
                },
                hyperlink: ({ node, children }) => {
                  const hyperlink = node as RTLinkNode;
                  const url = resolvePrismicLinkUrl(hyperlink);
                  const target = url.startsWith("/") ? undefined : "_blank";
                  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
                    trackHeaderNavLinkClick(
                      event,
                      url,
                      drawerTitle,
                      columnTitle
                    );

                  if (hyperlink.data?.link_type === "Document") {
                    return (
                      <GatsbyLink to={url} onClick={handleClick}>
                        {children}
                      </GatsbyLink>
                    );
                  }

                  return (
                    <a
                      href={url}
                      target={target}
                      rel={
                        target === "_blank" ? "noopener noreferrer" : undefined
                      }
                      onClick={handleClick}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            />
          </Flyout>
        );
      })}
    </>
  );
},
drawerColumnItemRichTextPropsAreEqual);

// This component contains all the markup and logic for the drawers
// and page links that appear on the header. Data is only passec
export const PureHeaderLinks = (props: IPureHeaderLinksProps) => {
  // Current open drawer
  const [openIndex, setOpenIndex] = useState(-1);
  const headerLinkRef: any = useRef(null);

  const [openDrawerItems, setOpenDrawerItems] = useState(-1);
  // Shared across all flyouts so only one nested list is open at a time on touch.
  const [openFlyoutKey, setOpenFlyoutKey] = useState<string | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(false);
  const canUseFlyoutToggle = isTouchDevice || isMobileNav;
  // Desktop, hover-capable pointer: reveal flyouts on hover, guarded by the
  // menu-aim safe area. Touch/mobile keeps the tap-to-toggle path below.
  const useHoverInteraction = !canUseFlyoutToggle;

  // Mirror open state into a ref so the safe-area timer reads the live value.
  const openFlyoutKeyRef = useRef<string | null>(null);
  useEffect(() => {
    openFlyoutKeyRef.current = openFlyoutKey;
  }, [openFlyoutKey]);

  const { trackMouseMove, requestActivate, cancelAndClose } = useFlyoutSafeArea(
    {
      enabled: useHoverInteraction,
      getOpenKey: useCallback(() => openFlyoutKeyRef.current, []),
      getOpenPanelRect: useCallback(() => {
        const key = openFlyoutKeyRef.current;
        if (!key) {
          return null;
        }
        return (
          document
            .getElementById(`header-flyout-${key}`)
            ?.getBoundingClientRect() ?? null
        );
      }, []),
      onActivate: useCallback(
        (key: string | null) => setOpenFlyoutKey(key),
        []
      ),
    }
  );

  const hoveredFlyoutTargetRef = useRef<string | null | undefined>(undefined);

  const handleColumnMouseMove = useCallback(
    (event: React.MouseEvent<HTMLUListElement>) => {
      trackMouseMove(event);

      if (!useHoverInteraction) {
        return;
      }

      const resolved = resolveFlyoutKeyFromTarget(event.target);
      if (
        resolved === undefined ||
        resolved === hoveredFlyoutTargetRef.current
      ) {
        return;
      }

      hoveredFlyoutTargetRef.current = resolved;
      requestActivate(resolved);
    },
    [trackMouseMove, requestActivate, useHoverInteraction]
  );

  const handleColumnMouseLeave = useCallback(() => {
    hoveredFlyoutTargetRef.current = undefined;
    cancelAndClose();
  }, [cancelAndClose]);

  useEffect(() => {
    if (!useHoverInteraction) {
      return;
    }

    document
      .querySelectorAll<HTMLElement>('[id^="header-flyout-trigger-"]')
      .forEach((trigger) => {
        const key = trigger.id.slice("header-flyout-trigger-".length);
        trigger.setAttribute(
          "aria-expanded",
          openFlyoutKey === key ? "true" : "false"
        );
      });
  }, [openFlyoutKey, useHoverInteraction]);

  const {
    headerLinks,
    headerLinksUID,
    setHeaderLinksData,
    isHeaderLinksDataSet,
    setIsDesktopNavOpen,
    registerEscapeHandler,
    registerNavCloseHandler,
    onRequestCloseMobileNav,
  } = props;

  const isDarkMode = props.isDarkMode || false;

  const headerEntries = headerLinks.allPrismicHeaderLinks.edges;

  const { currentTransitionPercent } = useThemeContext();

  const [headerHeight, setHeaderHeight] = useState(0);
  const [mainHeaderHeight, setMainHeaderHeight] = useState(0);

  useEffect(() => {
    const header = document.getElementById("site-header");
    const mainHeader = document.getElementById("site-main-header");

    const handleResize = () => {
      setTimeout(() => {
        if (header?.offsetHeight) {
          setHeaderHeight(header?.offsetHeight);
        }
        if (mainHeader?.offsetHeight) {
          setMainHeaderHeight(mainHeader?.offsetHeight);
        }
      }, 500);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const mobileQuery = window.matchMedia(MOBILE_NAV_QUERY);
    const updateMobileNav = () => setIsMobileNav(mobileQuery.matches);

    updateMobileNav();
    mobileQuery.addEventListener("change", updateMobileNav);

    return () => mobileQuery.removeEventListener("change", updateMobileNav);
  }, []);

  // Distinguish touch/coarse-pointer devices from hover-capable desktops.
  useEffect(() => {
    const touchQuery = window.matchMedia("(hover: none), (pointer: coarse)");
    const updateTouchDevice = () => setIsTouchDevice(touchQuery.matches);

    updateTouchDevice();
    touchQuery.addEventListener("change", updateTouchDevice);

    return () => touchQuery.removeEventListener("change", updateTouchDevice);
  }, []);

  const selectedEntry =
    headerEntries?.find((edge) => edge?.node?.uid === headerLinksUID) ||
    headerEntries?.find((edge) => edge?.node.uid === `default-links`);

  const drawers = getDrawerDataStructure(selectedEntry?.node);

  useEffect(() => {
    !isHeaderLinksDataSet &&
      setHeaderLinksData &&
      setHeaderLinksData({
        drawers: drawers,
        pageLinks: selectedEntry?.node.data?.page_links,
      });

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  });

  const handleOutsideClick = (e: MouseEvent) => {
    if (!headerLinkRef?.current?.contains(e.target)) {
      closeDrawer();
    }
  };

  const closeDrawer = useCallback(() => {
    setOpenIndex(-1);
    setOpenDrawerItems(-1);
    setOpenFlyoutKey(null);
  }, []);

  useEffect(() => {
    if (!registerEscapeHandler) return;

    const handleEscape = (): boolean => {
      if (openFlyoutKey !== null) {
        setOpenFlyoutKey(null);
        return true;
      }

      if (openDrawerItems !== -1) {
        setOpenDrawerItems(-1);
        return true;
      }

      if (openIndex !== -1) {
        closeDrawer();
        return true;
      }

      if (props.isOpen && onRequestCloseMobileNav) {
        onRequestCloseMobileNav();
        return true;
      }

      return false;
    };

    registerEscapeHandler(handleEscape);

    return () => registerEscapeHandler(null);
  }, [
    closeDrawer,
    onRequestCloseMobileNav,
    openDrawerItems,
    openFlyoutKey,
    openIndex,
    props.isOpen,
    registerEscapeHandler,
  ]);

  useEffect(() => {
    if (!registerNavCloseHandler) return;

    registerNavCloseHandler(closeDrawer);

    return () => registerNavCloseHandler(null);
  }, [closeDrawer, registerNavCloseHandler]);

  const handleLinkInteraction = (index: number) => {
    const drawer = drawers[index];

    if (openIndex !== index) {
      track("Header Nav Drawer Opened", {
        drawer_title: drawer?.title ?? "",
        drawer_index: index,
      });
      setOpenIndex(index);
    } else {
      track("Header Nav Drawer Closed", {
        drawer_title: drawer?.title ?? "",
        drawer_index: index,
      });
      setOpenIndex(-1);
    }

    setOpenDrawerItems(-1);
    setOpenFlyoutKey(null);
  };

  useEffect(() => {
    setIsDesktopNavOpen(openIndex !== -1);
  }, [openIndex]);

  return (
    <HeaderLinksBase
      isOpen={props.isOpen}
      showOverlay={openIndex !== -1}
      showAnnouncementBanner={props.showAnnouncementBanner}
      isDarkMode={isDarkMode}
      offsetTop={headerHeight}
    >
      <LinksContainer
        ref={headerLinkRef}
        isOpen={openIndex !== -1}
        isOpenMobile={props.isOpen}
        aria-label="Main navigation"
      >
        {drawers.map((drawer, drawerIndex) => {
          const drawerPanelId = `header-drawer-panel-${drawerIndex}`;
          const isDrawerOpen = openIndex === drawerIndex;

          return (
            drawer &&
            drawer.title && (
              <DrawerTitle
                key={drawerIndex}
                isOpen={isDrawerOpen}
                isDarkMode={isDarkMode}
                transitionPercent={currentTransitionPercent}
                offsetTop={headerHeight}
                offsetTopMainHeader={mainHeaderHeight}
              >
                <DrawerTitleLabel
                  isOpen={isDrawerOpen}
                  isDarkMode={isDarkMode}
                  transitionPercent={currentTransitionPercent}
                  offsetTop={headerHeight}
                  offsetTopMainHeader={mainHeaderHeight}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isDrawerOpen}
                  aria-haspopup="true"
                  aria-controls={drawerPanelId}
                  aria-label={`${drawer.title} menu`}
                  onClick={() => handleLinkInteraction(drawerIndex)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleLinkInteraction(drawerIndex);
                    }
                  }}
                >
                  {drawer.title}

                  {isDrawerOpen ? (
                    <HeaderLinkIcon
                      css={FA.AngleUpSolid}
                      aria-hidden="true"
                    ></HeaderLinkIcon>
                  ) : (
                    <HeaderLinkIcon
                      css={FA.AngleDownSolid}
                      aria-hidden="true"
                    ></HeaderLinkIcon>
                  )}
                  <HeaderLinkIconMobile
                    css={FA.AngleRightSolid}
                    aria-hidden="true"
                  ></HeaderLinkIconMobile>
                </DrawerTitleLabel>
                <DrawerColumnsContainer
                  className={clsx([isDrawerOpen ? "drawer open" : "drawer"])}
                  data-open-flyout={
                    isDrawerOpen ? openFlyoutKey ?? "" : undefined
                  }
                  id={drawerPanelId}
                  role="region"
                  aria-label={`${drawer.title} submenu`}
                  isOpen={isDrawerOpen}
                  isDarkMode={isDarkMode}
                  transitionPercent={currentTransitionPercent}
                  offsetTop={headerHeight}
                  offsetTopMainHeader={mainHeaderHeight}
                >
                  <BackButton
                    role="button"
                    tabIndex={0}
                    aria-label="Back to main navigation"
                    onClick={() => {
                      track("Header Nav Back Clicked");
                      closeDrawer();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        track("Header Nav Back Clicked");
                        closeDrawer();
                      }
                    }}
                  >
                    <i css={FA.AngleLeftRegular} aria-hidden="true"></i>
                  </BackButton>
                  <DrawerColumns
                    isOpen={isDrawerOpen}
                    isDarkMode={isDarkMode}
                    transitionPercent={currentTransitionPercent}
                    offsetTop={headerHeight}
                    offsetTopMainHeader={mainHeaderHeight}
                    minHeight={drawer.minHeight}
                    maxHeight={drawer.maxHeight}
                  >
                    {drawers[drawerIndex].columns.map((column, columnIndex) => {
                      const columnPanelId = `header-column-panel-${drawerIndex}-${columnIndex}`;
                      const isColumnOpen = openDrawerItems === columnIndex;
                      const columnTitle = column.primary?.column_title ?? "";
                      const columnLinkTarget =
                        column.primary?.column_link?.target;
                      const rawColumnLinkUrl = column.primary?.column_link?.url;
                      const columnLinkUrl = isNavigableUrl(rawColumnLinkUrl)
                        ? buildRelativeUrl(rawColumnLinkUrl)
                        : null;
                      const columnItems =
                        column.items?.filter((item) => item?.link) ?? [];

                      return (
                        column && (
                          <MeasuredDrawerColumn
                            key={columnIndex}
                            isDrawerOpen={isDrawerOpen}
                            $hasList={columnHasList(column.items)}
                          >
                            <DrawerColumnTitle
                              isOpen={isColumnOpen}
                              isDarkMode={isDarkMode}
                              transitionPercent={currentTransitionPercent}
                              offsetTop={headerHeight}
                              offsetTopMainHeader={mainHeaderHeight}
                              role={isMobileNav ? "button" : undefined}
                              tabIndex={isMobileNav ? 0 : undefined}
                              aria-expanded={
                                isMobileNav ? isColumnOpen : undefined
                              }
                              aria-controls={
                                isMobileNav ? columnPanelId : undefined
                              }
                              onClick={() => {
                                if (openDrawerItems === columnIndex) {
                                  track("Header Nav Column Collapsed", {
                                    drawer_title: drawer.title ?? "",
                                    column_title: columnTitle,
                                    column_index: columnIndex,
                                  });
                                  setOpenDrawerItems(-1);
                                } else {
                                  track("Header Nav Column Expanded", {
                                    drawer_title: drawer.title ?? "",
                                    column_title: columnTitle,
                                    column_index: columnIndex,
                                  });
                                  setOpenDrawerItems(columnIndex);
                                  // Collapse any open nested flyout when switching columns on mobile.
                                  setOpenFlyoutKey(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (!isMobileNav) return;

                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  if (openDrawerItems === columnIndex) {
                                    setOpenDrawerItems(-1);
                                  } else {
                                    setOpenDrawerItems(columnIndex);
                                    setOpenFlyoutKey(null);
                                  }
                                }
                              }}
                            >
                              {columnLinkUrl ? (
                                <a
                                  href={columnLinkUrl}
                                  target={columnLinkTarget || undefined}
                                  rel={
                                    columnLinkTarget === "_blank"
                                      ? "noopener noreferrer"
                                      : undefined
                                  }
                                  onClick={(e) =>
                                    trackHeaderNavLinkClick(
                                      e,
                                      columnLinkUrl,
                                      drawer.title,
                                      columnTitle
                                    )
                                  }
                                >
                                  {columnTitle}
                                </a>
                              ) : (
                                columnTitle
                              )}
                            </DrawerColumnTitle>
                            {column.items && (
                              <DrawerColumnItems
                                id={isMobileNav ? columnPanelId : undefined}
                                onMouseMove={handleColumnMouseMove}
                                onMouseLeave={handleColumnMouseLeave}
                                style={{
                                  columns: column.primary?.column_count || 1,
                                }}
                              >
                                {columnItems.map((item, itemIndex) => {
                                  return (
                                    <DrawerColumnItem key={itemIndex}>
                                      <DrawerColumnItemLink
                                        onClick={closeDrawer}
                                      >
                                        <DrawerColumnItemRichText
                                          field={item.link.richText}
                                          linkId={`${drawerIndex}-${columnIndex}-${itemIndex}`}
                                          canUseFlyoutToggle={
                                            canUseFlyoutToggle
                                          }
                                          openFlyoutKey={
                                            canUseFlyoutToggle
                                              ? openFlyoutKey
                                              : null
                                          }
                                          setOpenFlyoutKey={setOpenFlyoutKey}
                                          drawerTitle={drawer.title}
                                          columnTitle={
                                            column.primary?.column_title
                                          }
                                          flyoutListAlignment={getFlyoutListAlignment(
                                            itemIndex,
                                            columnItems.length
                                          )}
                                        />
                                      </DrawerColumnItemLink>
                                    </DrawerColumnItem>
                                  );
                                })}
                              </DrawerColumnItems>
                            )}
                          </MeasuredDrawerColumn>
                        )
                      );
                    })}
                  </DrawerColumns>
                </DrawerColumnsContainer>
              </DrawerTitle>
            )
          );
        })}

        {selectedEntry?.node.data?.page_links?.map((pageLink, index) => {
          const pageLinkUrl = pageLink?.link?.url;

          return (
            pageLink?.link_text &&
            pageLinkUrl && (
              <li key={index}>
                <PageLink
                  href={buildRelativeUrl(pageLinkUrl)}
                  target={pageLink.link?.target || ""}
                  isDarkMode={isDarkMode}
                  transitionPercent={currentTransitionPercent}
                  isOpen={openIndex !== -1}
                  offsetTop={headerHeight}
                  offsetTopMainHeader={mainHeaderHeight}
                  onClick={() => {
                    track("Header Nav Link Clicked", {
                      link_text: pageLink.link_text ?? "",
                      link_url: buildRelativeUrl(pageLinkUrl),
                      link_location: "page",
                    });
                  }}
                >
                  {pageLink.link_text}
                </PageLink>
              </li>
            )
          );
        })}
      </LinksContainer>
    </HeaderLinksBase>
  );
};
